/**
 * SFX module: M5.T7 + M5.T8 + M5.T9.
 *
 * Per IMPLEMENTATION_PLAN.md §7 M5.T7-T9:
 *   - 3 sounds: correct yip + bell, wrong nope, round-end swoosh + bell.
 *   - Web Audio API integration, preloaded on first user interaction.
 *   - Settings toggle in lobby + in-game.
 *   - Sound default ON.
 *
 * (T16 (4.2) removed the brush-rasp voice + its opt-in toggle: `brushRasp`
 *  was synthesized but `playSfx('brushRasp')` was never called anywhere, so
 *  the store, gate, and toggle were dead weight.)
 *
 * Sourcing decision (M5.T7): assets are **procedural**, synthesized
 * inline from WebAudio oscillators + envelopes + filtered noise. No
 * audio files ship. Tradeoffs:
 *
 *   + Zero licensing risk (owned by definition).
 *   + Zero bundle cost.
 *   + Editable in code: every voice is one ~30-line function below.
 *   - Less expressive than commissioned audio. Real recorded SFX can
 *     replace these by swapping the synth functions; the public surface
 *     (`playSfx`, `$sfxMuted`) stays the same.
 *
 * Settings are persisted in localStorage and surfaced as nanostores so
 * the UI re-renders on change without prop-drilling.
 */

import { atom } from "nanostores";

export type SfxName = "correct" | "wrong" | "roundSwoosh";

const LS_KEY_MUTED = "drawlol:sfx:muted";

// ---------------------------------------------------------------------------
// Settings: persisted, reactive

function loadBool(key: string, defaultValue: boolean): boolean {
  if (typeof localStorage === "undefined") return defaultValue;
  const raw = localStorage.getItem(key);
  if (raw === null) return defaultValue;
  return raw === "true";
}

function saveBool(key: string, value: boolean): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // private mode / quota: ignore
  }
}

/** Master mute. Sound default is ON (per M5.T9) → muted=false. */
export const $sfxMuted = atom<boolean>(loadBool(LS_KEY_MUTED, false));
$sfxMuted.subscribe((v) => saveBool(LS_KEY_MUTED, v));

// ---------------------------------------------------------------------------
// AudioContext: lazy, gesture-gated

let _ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AC =
    (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) {
    try {
      _ctx = new AC();
    } catch {
      return null;
    }
  }
  if (_ctx.state === "suspended") {
    // Browser autoplay policy: resume on first user gesture. Calls
    // outside a gesture are silently no-op.
    void _ctx.resume().catch(() => {});
  }
  return _ctx;
}

// Test seam: let unit tests reset the cached context.
export function _resetSfxContextForTests(): void {
  _ctx = null;
}

// ---------------------------------------------------------------------------
// Synth voices

/** Quick attack-decay envelope on a GainNode. */
function envelope(
  ctx: AudioContext,
  gain: GainNode,
  startGain: number,
  peakGain: number,
  attackMs: number,
  decayMs: number,
): void {
  const t0 = ctx.currentTime;
  gain.gain.cancelScheduledValues(t0);
  gain.gain.setValueAtTime(startGain, t0);
  gain.gain.linearRampToValueAtTime(peakGain, t0 + attackMs / 1000);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + (attackMs + decayMs) / 1000);
}

/** Make a short white-noise buffer. */
function makeNoiseBuffer(ctx: AudioContext, durationMs: number): AudioBuffer {
  const len = Math.max(1, Math.floor((ctx.sampleRate * durationMs) / 1000));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

function synthCorrect(ctx: AudioContext): void {
  // Rising sine yip (500 → 900 Hz over 80ms) + a triangle bell harmonic
  // at 1500 Hz starting 40ms in.
  const out = ctx.destination;
  const t0 = ctx.currentTime;

  const yip = ctx.createOscillator();
  yip.type = "sine";
  yip.frequency.setValueAtTime(500, t0);
  yip.frequency.exponentialRampToValueAtTime(900, t0 + 0.08);
  const yipGain = ctx.createGain();
  envelope(ctx, yipGain, 0, 0.25, 5, 100);
  yip.connect(yipGain).connect(out);
  yip.start(t0);
  yip.stop(t0 + 0.2);

  const bell = ctx.createOscillator();
  bell.type = "triangle";
  bell.frequency.setValueAtTime(1500, t0 + 0.04);
  const bellGain = ctx.createGain();
  bellGain.gain.setValueAtTime(0, t0);
  bellGain.gain.setValueAtTime(0, t0 + 0.04);
  bellGain.gain.linearRampToValueAtTime(0.18, t0 + 0.07);
  bellGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
  bell.connect(bellGain).connect(out);
  bell.start(t0 + 0.04);
  bell.stop(t0 + 0.35);
}

function synthWrong(ctx: AudioContext): void {
  // Short descending square (260 → 160 Hz, 100ms). Slightly damp.
  const out = ctx.destination;
  const t0 = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = "square";
  osc.frequency.setValueAtTime(260, t0);
  osc.frequency.exponentialRampToValueAtTime(160, t0 + 0.1);
  const filt = ctx.createBiquadFilter();
  filt.type = "lowpass";
  filt.frequency.setValueAtTime(1200, t0);
  const gain = ctx.createGain();
  envelope(ctx, gain, 0, 0.16, 4, 120);
  osc.connect(filt).connect(gain).connect(out);
  osc.start(t0);
  osc.stop(t0 + 0.18);
}

function synthRoundSwoosh(ctx: AudioContext): void {
  // White-noise burst through a band-pass sweep + trailing soft bell.
  const out = ctx.destination;
  const t0 = ctx.currentTime;

  const noise = ctx.createBufferSource();
  noise.buffer = makeNoiseBuffer(ctx, 300);
  const filt = ctx.createBiquadFilter();
  filt.type = "bandpass";
  filt.Q.setValueAtTime(1, t0);
  filt.frequency.setValueAtTime(2200, t0);
  filt.frequency.exponentialRampToValueAtTime(500, t0 + 0.3);
  const noiseGain = ctx.createGain();
  envelope(ctx, noiseGain, 0, 0.22, 10, 280);
  noise.connect(filt).connect(noiseGain).connect(out);
  noise.start(t0);
  noise.stop(t0 + 0.32);

  const bell = ctx.createOscillator();
  bell.type = "sine";
  bell.frequency.setValueAtTime(680, t0 + 0.2);
  const bellGain = ctx.createGain();
  bellGain.gain.setValueAtTime(0, t0);
  bellGain.gain.setValueAtTime(0, t0 + 0.2);
  bellGain.gain.linearRampToValueAtTime(0.12, t0 + 0.22);
  bellGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.5);
  bell.connect(bellGain).connect(out);
  bell.start(t0 + 0.2);
  bell.stop(t0 + 0.52);
}

const SYNTHS: Record<SfxName, (ctx: AudioContext) => void> = {
  correct: synthCorrect,
  wrong: synthWrong,
  roundSwoosh: synthRoundSwoosh,
};

// ---------------------------------------------------------------------------
// Public API

/**
 * Play a sound effect. No-ops if:
 *   - SFX are muted ($sfxMuted)
 *   - WebAudio is unavailable (server-side render, ancient browser)
 *
 * Safe to call outside a user gesture, the context starts suspended
 * and resumes on the next gesture, which means the very first sound
 * may not be audible. That's an acceptable cost for the simpler API.
 */
export function playSfx(name: SfxName): void {
  if ($sfxMuted.get()) return;
  const ctx = getCtx();
  if (!ctx) return;
  try {
    SYNTHS[name](ctx);
  } catch {
    // WebAudio engine threw: ignore so callers aren't burdened with
    // a sound-system error path.
  }
}
